// AdminConsumablesPanel.tsx — Rallify site-admin editor for the consumable heat
// config (accepted items per category) per game. Guild officers can't touch this;
// it feeds every guild's consumable import scoring.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { API_URL } from "./config";

type GameRow = { id: string; code: string; name: string; is_active: boolean };

type SpellRow = { id: string; spell_id: number; label: string };

type CategoryRow = {
  id: string;
  key: string;
  label: string;
  applies_to: string[];
  is_bonus: boolean;
  sort: number;
  detection: "cast" | "aura";
  aura_name: string | null;
  spells: SpellRow[];
};

const ROLE_LABEL: Record<string, string> = {
  tank: "Tank",
  healer: "Healer",
  melee_dps: "Melee",
  ranged_dps: "Ranged",
};

async function api(path: string, options?: RequestInit) {
  const res = await fetch(`${API_URL}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-[11px]">
      <div className="mb-1 font-semibold text-[var(--c-ink-2)] uppercase tracking-wide">{label}</div>
      {children}
    </label>
  );
}

const inputCls =
  "w-full rounded border border-[var(--c-line)] bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)] px-2 py-1.5 text-xs text-[var(--c-ink)] placeholder:text-[var(--c-ink-4)] focus:outline-none focus:border-amber-500/40";

function CategoryEditor({
  gameId,
  category,
  heatRoles,
  detections,
  onChange,
  onDelete,
}: {
  gameId: string;
  category: CategoryRow;
  heatRoles: string[];
  detections: string[];
  onChange: (c: CategoryRow) => void;
  onDelete: (id: string) => void;
}) {
  const [draft, setDraft] = useState(category);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newSpellId, setNewSpellId] = useState("");
  const [newSpellLabel, setNewSpellLabel] = useState("");

  useEffect(() => setDraft(category), [category]);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(category), [draft, category]);

  const saveCategory = async () => {
    setSaving(true);
    setError(null);
    try {
      const data = await api(`/admin/consumables/categories/${category.id}`, {
        method: "PUT",
        body: JSON.stringify({
          key: draft.key,
          label: draft.label,
          applies_to: draft.applies_to,
          is_bonus: draft.is_bonus,
          sort: draft.sort,
          detection: draft.detection,
          aura_name: draft.aura_name,
        }),
      });
      onChange({ ...draft, ...data, spells: category.spells });
    } catch (e: any) {
      setError(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleRole = (role: string) => {
    setDraft((d) => ({
      ...d,
      applies_to: d.applies_to.includes(role) ? d.applies_to.filter((r) => r !== role) : [...d.applies_to, role],
    }));
  };

  const addSpell = async () => {
    const sid = Number(newSpellId);
    if (!Number.isInteger(sid) || sid <= 0 || !newSpellLabel.trim()) return;
    setError(null);
    try {
      const data = await api(`/admin/consumables/categories/${category.id}/spells`, {
        method: "POST",
        body: JSON.stringify({ spell_id: sid, label: newSpellLabel.trim() }),
      });
      onChange({ ...category, spells: [...category.spells, data].sort((a, b) => a.spell_id - b.spell_id) });
      setNewSpellId("");
      setNewSpellLabel("");
    } catch (e: any) {
      setError(e?.message || "Add spell failed");
    }
  };

  const updateSpell = async (spell: SpellRow, label: string) => {
    try {
      const data = await api(`/admin/consumables/spells/${spell.id}`, {
        method: "PUT",
        body: JSON.stringify({ spell_id: spell.spell_id, label }),
      });
      onChange({ ...category, spells: category.spells.map((s) => (s.id === spell.id ? data : s)) });
    } catch (e: any) {
      setError(e?.message || "Update spell failed");
    }
  };

  const deleteSpell = async (spell: SpellRow) => {
    try {
      await api(`/admin/consumables/spells/${spell.id}`, { method: "DELETE" });
      onChange({ ...category, spells: category.spells.filter((s) => s.id !== spell.id) });
    } catch (e: any) {
      setError(e?.message || "Delete spell failed");
    }
  };

  return (
    <div className="rounded-xl border border-[var(--c-line)] bg-[var(--c-bg-veil)] p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 flex-1">
          <Labeled label="Key">
            <input className={inputCls} value={draft.key} onChange={(e) => setDraft((d) => ({ ...d, key: e.target.value }))} />
          </Labeled>
          <Labeled label="Label (shown to officers)">
            <input className={inputCls} value={draft.label} onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))} />
          </Labeled>
          <Labeled label="Detection">
            <select
              className={inputCls}
              value={draft.detection}
              onChange={(e) => setDraft((d) => ({ ...d, detection: e.target.value as "cast" | "aura" }))}
            >
              {detections.map((det) => (
                <option key={det} value={det}>
                  {det === "cast" ? "Cast (potions, runes)" : "Aura (flasks, elixirs, food)"}
                </option>
              ))}
            </select>
          </Labeled>
          <Labeled label="Sort">
            <input
              type="number"
              className={inputCls}
              value={draft.sort}
              onChange={(e) => setDraft((d) => ({ ...d, sort: Number(e.target.value) }))}
            />
          </Labeled>
          <Labeled label="Applies to roles">
            <div className="flex flex-wrap gap-2 pt-1">
              {heatRoles.map((role) => (
                <label key={role} className="flex items-center gap-1 text-[11px] text-[var(--c-ink-2)]">
                  <input type="checkbox" checked={draft.applies_to.includes(role)} onChange={() => toggleRole(role)} className="accent-amber-500" />
                  {ROLE_LABEL[role] || role}
                </label>
              ))}
            </div>
          </Labeled>
          <Labeled label="Bonus category">
            <label className="flex items-center gap-2 text-[11px] text-[var(--c-ink-2)] pt-1">
              <input type="checkbox" checked={draft.is_bonus} onChange={(e) => setDraft((d) => ({ ...d, is_bonus: e.target.checked }))} className="accent-amber-500" />
              Counts as bonus (up to +2, outside the denominator)
            </label>
          </Labeled>
          {draft.detection === "aura" && (
            <Labeled label="Aura name match (optional, e.g. Well Fed)">
              <input
                className={inputCls}
                value={draft.aura_name || ""}
                onChange={(e) => setDraft((d) => ({ ...d, aura_name: e.target.value || null }))}
                placeholder="Well Fed"
              />
            </Labeled>
          )}
        </div>
        <button
          type="button"
          onClick={() => onDelete(category.id)}
          className="text-[10px] text-red-600/70 dark:text-red-400/70 hover:text-red-700 dark:hover:text-red-300 whitespace-nowrap"
        >
          Delete category
        </button>
      </div>

      {error && <div className="text-[11px] text-red-600/95 dark:text-red-400/95">{error}</div>}

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={saveCategory}
          className="rounded-lg bg-amber-500/20 border border-amber-500/35 px-3 py-1.5 text-[11px] font-bold text-amber-800 dark:text-amber-100 hover:bg-amber-500/30 disabled:opacity-40"
        >
          {saving ? "Saving…" : dirty ? "Save category" : "Saved"}
        </button>
      </div>

      <div className="rounded-lg border border-[var(--c-line-soft)] overflow-hidden">
        <div className="px-3 py-1.5 bg-[var(--c-surface)] text-[10px] font-bold uppercase tracking-wider text-[var(--c-ink-3)]">
          Tracked spell IDs ({category.spells.length})
        </div>
        <table className="w-full text-left text-[11px]">
          <tbody>
            {category.spells.map((s) => (
              <tr key={s.id} className="border-b border-[var(--c-line-soft)] last:border-0">
                <td className="px-3 py-1 w-24 font-mono text-[var(--c-ink-2)]">{s.spell_id}</td>
                <td className="px-3 py-1">
                  <input
                    className="w-full bg-transparent text-[var(--c-ink)] focus:outline-none focus:bg-[var(--c-surface)] rounded px-1"
                    defaultValue={s.label}
                    onBlur={(e) => e.target.value.trim() && e.target.value !== s.label && updateSpell(s, e.target.value.trim())}
                  />
                </td>
                <td className="px-3 py-1 text-right w-16">
                  <button type="button" onClick={() => deleteSpell(s)} className="text-[10px] text-red-600/60 dark:text-red-400/60 hover:text-red-700 dark:hover:text-red-300">
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            <tr>
              <td className="px-3 py-1.5">
                <input
                  className={inputCls}
                  placeholder="Spell ID"
                  value={newSpellId}
                  onChange={(e) => setNewSpellId(e.target.value.replace(/[^0-9]/g, ""))}
                />
              </td>
              <td className="px-3 py-1.5">
                <input className={inputCls} placeholder="Label (e.g. Elixir of Major Agility)" value={newSpellLabel} onChange={(e) => setNewSpellLabel(e.target.value)} />
              </td>
              <td className="px-3 py-1.5 text-right">
                <button
                  type="button"
                  onClick={addSpell}
                  disabled={!newSpellId || !newSpellLabel.trim()}
                  className="rounded bg-emerald-500/20 border border-emerald-500/35 px-2 py-1 text-[10px] font-bold text-emerald-800 dark:text-emerald-100 hover:bg-emerald-500/30 disabled:opacity-40"
                >
                  Add
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminConsumablesPanel() {
  const [games, setGames] = useState<GameRow[]>([]);
  const [gameId, setGameId] = useState<string | null>(null);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [heatRoles, setHeatRoles] = useState<string[]>(["tank", "healer", "melee_dps", "ranged_dps"]);
  const [detections, setDetections] = useState<string[]>(["cast", "aura"]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [newKey, setNewKey] = useState("");
  const [newLabel, setNewLabel] = useState("");

  useEffect(() => {
    api("/admin/games")
      .then((rows: GameRow[]) => {
        setGames(rows);
        if (rows.length && !gameId) setGameId(rows[0].id);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConfig = useCallback(async (gid: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api(`/admin/games/${gid}/consumables`);
      setCategories(Array.isArray(data.categories) ? data.categories : []);
      if (Array.isArray(data.heat_roles)) setHeatRoles(data.heat_roles);
      if (Array.isArray(data.detections)) setDetections(data.detections);
    } catch (e: any) {
      setError(e?.message || "Load failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (gameId) loadConfig(gameId);
  }, [gameId, loadConfig]);

  const addCategory = async () => {
    if (!gameId || !newKey.trim() || !newLabel.trim()) return;
    setError(null);
    try {
      const data = await api(`/admin/games/${gameId}/consumables/categories`, {
        method: "POST",
        body: JSON.stringify({
          key: newKey.trim(),
          label: newLabel.trim(),
          applies_to: heatRoles,
          is_bonus: false,
          sort: categories.length + 1,
          detection: "cast",
        }),
      });
      setCategories((prev) => [...prev, { ...data, spells: [] }]);
      setNewKey("");
      setNewLabel("");
    } catch (e: any) {
      setError(e?.message || "Create failed");
    }
  };

  const deleteCategory = async (id: string) => {
    if (!window.confirm("Delete this category and all its tracked spells? This affects consumable scoring for every guild on this game.")) return;
    try {
      await api(`/admin/consumables/categories/${id}`, { method: "DELETE" });
      setCategories((prev) => prev.filter((c) => c.id !== id));
    } catch (e: any) {
      setError(e?.message || "Delete failed");
    }
  };

  return (
    <div className="space-y-5 max-w-5xl">
      <div>
        <h2 className="text-lg font-bold text-[var(--c-ink)]">Consumable heat config</h2>
        <p className="text-xs text-[var(--c-ink-3)] mt-1 max-w-3xl leading-relaxed">
          Define which consumables Rallify accepts per game, grouped into categories, and link the spell IDs that count for each.
          This is the master list every guild's consumable import scores against. Cast detection is for potions and runes; aura
          detection reconstructs buff uptime for flasks, elixirs and food (an aura name match like "Well Fed" catches every variant).
        </p>
      </div>

      <div className="flex items-center gap-3">
        <Labeled label="Game">
          <select className={inputCls} value={gameId || ""} onChange={(e) => setGameId(e.target.value)}>
            {games.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </Labeled>
      </div>

      {error && <div className="text-xs text-red-600/95 dark:text-red-400/95">{error}</div>}
      {loading ? (
        <div className="text-xs text-[var(--c-ink-4)]">Loading…</div>
      ) : (
        <div className="space-y-3">
          {categories.map((c) => (
            <CategoryEditor
              key={c.id}
              gameId={gameId!}
              category={c}
              heatRoles={heatRoles}
              detections={detections}
              onChange={(next) => setCategories((prev) => prev.map((x) => (x.id === next.id ? next : x)))}
              onDelete={deleteCategory}
            />
          ))}

          <div className="rounded-xl border border-dashed border-[var(--c-line)] bg-[var(--c-bg-veil)] p-4">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--c-ink-3)] mb-2">Add category</div>
            <div className="flex flex-wrap gap-2 items-end">
              <div className="w-40">
                <Labeled label="Key">
                  <input className={inputCls} value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="elixirs" />
                </Labeled>
              </div>
              <div className="flex-1 min-w-[200px]">
                <Labeled label="Label">
                  <input className={inputCls} value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Battle Elixirs" />
                </Labeled>
              </div>
              <button
                type="button"
                onClick={addCategory}
                disabled={!newKey.trim() || !newLabel.trim()}
                className="rounded-lg bg-emerald-500/20 border border-emerald-500/35 px-4 py-2 text-xs font-bold text-emerald-800 dark:text-emerald-100 hover:bg-emerald-500/30 disabled:opacity-40"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
